import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Stack, Typography, Button, Card, Alert, Chip,
  CircularProgress, TextField, Divider, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SaveIcon from '@mui/icons-material/Save';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import ImportarSueldosDialog from './ImportarSueldosDialog';

import { useAuth } from '../../context/AuthContext';
import {
  usePlanilla,
  usePlanillaDetalle,
  useGuardarDetalle,
  useConfirmarDDJJ,
  useReabrirDDJJ,
  useDeleteDDJJ,
  useNominaFaltante,
  useSincronizarNomina,
  usePagosNoDefinitivosDDJJ,
  type SincronizarNominaResult,
} from '../../hooks/useDDJJ';
import { useCuponPorPlanilla } from '../../hooks/useCupones';
import { usePagosPorCupon } from '../../hooks/usePagos';
import { esPagoDefinitivo, esPagoNoDefinitivo } from '../../types/pago';
import { mapSupabaseError } from '../../utils/errores';
import { formatCurrency, formatDate } from '../../utils/format';
import { MONTO_MAX_DEFAULT } from '../../utils/validations';
import {
  type PlanillaDetalle,
  getEstadoLabel,
  getEstadoColor,
} from '../../types/ddjj';

// ── Component ─────────────────────────────────────────────────────────────────

export default function DDJJDetalle() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { effectiveRole } = useAuth();
  const esAdminGeneral = effectiveRole === 'admin_general';

  const { data: planilla, isLoading: loadingPlanilla, isError: errorPlanilla } =
    usePlanilla(id);
  const { data: detalle = [], isLoading: loadingDetalle } =
    usePlanillaDetalle(id);
  const { data: cuponVigente } = useCuponPorPlanilla(id);
  const { data: pagosCupon = [] } = usePagosPorCupon(cuponVigente?.id);

  const guardarDetalle = useGuardarDetalle();
  const confirmarDDJJ = useConfirmarDDJJ();
  const reabrirDDJJ = useReabrirDDJJ();
  const deleteDDJJ = useDeleteDDJJ();
  const sincronizarNomina = useSincronizarNomina();

  const esBorrador = planilla?.estado === 'borrador';
  const esConfirmada = planilla?.estado === 'confirmada';

  // Reapertura: solo admin_general, planilla confirmada, cupón vigente pendiente
  // y SIN pagos definitivos (acreditados / conciliados con Pronto Pago). Los pagos
  // pendientes no bloquean: se rechazan solos al reabrir; los rechazados tampoco
  // bloquean (ADR #044).
  const pagosDefinitivos = pagosCupon.filter(esPagoDefinitivo);
  const pagosPorRechazar = pagosCupon.filter(esPagoNoDefinitivo);
  const puedeReabrir =
    esAdminGeneral &&
    esConfirmada &&
    !!cuponVigente &&
    cuponVigente.estado === 'pendiente' &&
    pagosDefinitivos.length === 0;

  // ── Edición de sueldos (estado local) ────────────────────────────────────

  /**
   * Mapa de sueldo editado por rowId.
   * Inicializado con los valores de la BD cuando carga el detalle.
   */
  const [sueldoMap, setSueldoMap] = useState<Record<string, string>>({});

  useEffect(() => {
    if (detalle.length > 0) {
      const init: Record<string, string> = {};
      detalle.forEach((row) => {
        init[row.id] = String(row.sueldo ?? 0);
      });
      setSueldoMap(init);
    }
  }, [detalle]);

  const getSueldo = useCallback(
    (rowId: string) => parseFloat(sueldoMap[rowId] ?? '0') || 0,
    [sueldoMap]
  );

  const getAporte = useCallback(
    (rowId: string, porcentaje: number | null) => {
      const s = getSueldo(rowId);
      return Math.round(s * (porcentaje ?? 0)) / 100;
    },
    [getSueldo]
  );

  const montoTotal = detalle.reduce(
    (sum, row) => sum + getAporte(row.id, row.porcentaje_aporte),
    0
  );

  /** ¿Hay cambios no guardados? */
  const isDirty = detalle.some(
    (row) => getSueldo(row.id) !== (row.sueldo ?? 0)
  );

  // ── Confirm state ─────────────────────────────────────────────────────────

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenMotivo, setReopenMotivo] = useState('');
  const [reopenError, setReopenError] = useState<string | null>(null);
  const [sueldoErrors, setSueldoErrors] = useState<string[]>([]);
  const [openImport, setOpenImport] = useState(false);
  const [syncResultado, setSyncResultado] = useState<SincronizarNominaResult | null>(null);

  // Empleados activos que no están en la nómina del borrador (ADR #044)
  const { data: faltantes = [] } = useNominaFaltante(planilla, detalle);
  // Pagos pendientes que se rechazarían al eliminar (solo se consulta con el diálogo abierto)
  const { data: pagosPorRechazarAlEliminar = 0 } = usePagosNoDefinitivosDDJJ(id, deleteConfirmOpen);

  // ── Validación de sueldos (defensa contra overflow numeric(14,2) — #031) ─

  /**
   * Devuelve lista de mensajes de error si algún sueldo no es válido.
   * Vacío si todo OK. Casos:
   *   - negativo
   *   - excede MONTO_MAX_DEFAULT (~ 1.000 millones)
   *   - NaN (input no numérico)
   */
  const validarSueldos = useCallback((): string[] => {
    const errs: string[] = [];
    for (const row of detalle) {
      const raw = sueldoMap[row.id] ?? '0';
      const s = parseFloat(raw);
      if (raw !== '' && isNaN(s)) {
        errs.push(`${row.apellido}, ${row.nombre}: el sueldo no es un número válido`);
      } else if (s < 0) {
        errs.push(`${row.apellido}, ${row.nombre}: el sueldo no puede ser negativo`);
      } else if (s > MONTO_MAX_DEFAULT) {
        errs.push(
          `${row.apellido}, ${row.nombre}: el sueldo supera el máximo permitido ($${MONTO_MAX_DEFAULT.toLocaleString('es-AR')})`,
        );
      }
    }
    return errs;
  }, [detalle, sueldoMap]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleGuardar = async () => {
    if (!id || !esBorrador) return;
    const errs = validarSueldos();
    if (errs.length > 0) {
      setSueldoErrors(errs);
      return;
    }
    setSueldoErrors([]);
    const rows = detalle.map((row) => ({
      id: row.id,
      sueldo: getSueldo(row.id),
      porcentaje_aporte: row.porcentaje_aporte ?? 0,
    }));
    await guardarDetalle.mutateAsync({ planilla_id: id, rows });
  };

  const handleConfirmar = async () => {
    if (!id) return;
    // Si hay cambios sin guardar, los guarda primero
    if (isDirty) await handleGuardar();
    const result = await confirmarDDJJ.mutateAsync({ id, monto_total: montoTotal });
    setConfirmOpen(false);
    // Navegar al cupón generado automáticamente
    navigate(`/cupones/${result.cupon.id}`);
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteDDJJ.mutateAsync(id);
      navigate('/ddjj');
    } catch {
      // se muestra en el diálogo vía deleteDDJJ.error (ticket #699: antes no pasaba nada)
    }
  };

  const handleSincronizar = async () => {
    if (!id || !esBorrador) return;
    setSyncResultado(null);
    // Los sueldos sin guardar se guardan primero: al sincronizar se recarga la
    // nómina y se perderían. Si hay sueldos inválidos, se frena acá.
    if (isDirty) {
      const errs = validarSueldos();
      if (errs.length > 0) {
        setSueldoErrors(errs);
        return;
      }
      await handleGuardar();
    }
    try {
      setSyncResultado(await sincronizarNomina.mutateAsync(id));
    } catch {
      // se muestra abajo vía sincronizarNomina.error
    }
  };

  const handleReabrir = async () => {
    if (!id) return;
    const motivo = reopenMotivo.trim();
    if (motivo.length < 10) {
      setReopenError('El motivo debe tener al menos 10 caracteres.');
      return;
    }
    setReopenError(null);
    try {
      await reabrirDDJJ.mutateAsync({ id, motivo });
      setReopenOpen(false);
      setReopenMotivo('');
    } catch {
      // se muestra abajo vía reabrirDDJJ.error
    }
  };

  // ── Columns ───────────────────────────────────────────────────────────────

  const columns: GridColDef<PlanillaDetalle>[] = [
    {
      field: 'nombre_completo',
      headerName: 'Apellido y nombre',
      flex: 2,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams<PlanillaDetalle>) =>
        params.row ? `${params.row.apellido}, ${params.row.nombre}` : '',
    },
    {
      field: 'cuil',
      headerName: 'CUIL',
      width: 130,
    },
    {
      field: 'categoria',
      headerName: 'Categoría',
      width: 170,
      renderCell: (params: GridRenderCellParams<PlanillaDetalle>) =>
        params.row?.categoria?.descripcion ?? '-',
    },
    {
      field: 'es_afiliado',
      headerName: 'Afiliado',
      width: 90,
      renderCell: (params: GridRenderCellParams<PlanillaDetalle, boolean>) => (
        <Chip
          label={params.value ? 'Sí' : 'No'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'sueldo',
      headerName: 'Sueldo',
      width: 160,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params: GridRenderCellParams<PlanillaDetalle>) => {
        if (!esBorrador) {
          return (
            <Typography variant="body2">
              {formatCurrency(params.row.sueldo)}
            </Typography>
          );
        }
        const raw = sueldoMap[params.row.id] ?? '0';
        const parsed = parseFloat(raw);
        // Marca rojo si supera el tope o es negativo (#031)
        const tieneError =
          raw !== '' && (isNaN(parsed) || parsed < 0 || parsed > MONTO_MAX_DEFAULT);
        return (
          <TextField
            size="small"
            type="number"
            value={raw}
            onChange={(e) =>
              setSueldoMap((prev) => ({ ...prev, [params.row.id]: e.target.value }))
            }
            error={tieneError}
            inputProps={{
              min: 0,
              max: MONTO_MAX_DEFAULT,
              step: 100,
              style: { textAlign: 'right' },
              'aria-label': `Sueldo de ${params.row.apellido}, ${params.row.nombre}`,
            }}
            sx={{ width: 130 }}
            onClick={(e) => e.stopPropagation()}
          />
        );
      },
    },
    {
      field: 'porcentaje_aporte',
      headerName: '%',
      width: 70,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params: GridRenderCellParams<PlanillaDetalle>) => (
        <Typography variant="body2">
          {params.row.porcentaje_aporte != null ? `${params.row.porcentaje_aporte}%` : '-'}
        </Typography>
      ),
    },
    {
      field: 'aporte',
      headerName: 'Aporte',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params: GridRenderCellParams<PlanillaDetalle>) => (
        <Typography variant="body2" fontWeight={500}>
          {formatCurrency(getAporte(params.row.id, params.row.porcentaje_aporte))}
        </Typography>
      ),
    },
  ];

  // ── Loading / Error ───────────────────────────────────────────────────────

  if (loadingPlanilla) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (errorPlanilla || !planilla) {
    return (
      <Alert severity="error">
        No se pudo cargar la declaración jurada.
      </Alert>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Navegación */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/ddjj')}
        sx={{ mb: 2 }}
      >
        Volver a DDJJ
      </Button>

      {/* Encabezado */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
        >
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {planilla.empresa?.razon_social ?? '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Período: <strong>{planilla.periodo?.periodo ?? '—'}</strong>
              {planilla.periodo?.descripcion
                ? ` — ${planilla.periodo.descripcion}`
                : ''}
              {planilla.numero > 1
                ? ` · DDJJ adicional #${planilla.numero}`
                : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Fecha de carga: {formatDate(planilla.fecha_carga)}
              {planilla.fecha_confirmacion &&
                ` · Confirmada: ${formatDate(planilla.fecha_confirmacion)}`}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={getEstadoLabel(planilla.estado)}
              color={getEstadoColor(planilla.estado)}
            />
            {puedeReabrir && (
              <Tooltip title="Solo admin general · cupón pendiente sin pagos acreditados">
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<LockOpenIcon />}
                  onClick={() => setReopenOpen(true)}
                >
                  Reabrir DDJJ
                </Button>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {esConfirmada && esAdminGeneral && !puedeReabrir && cuponVigente && (
          <Alert severity="info" sx={{ mt: 2, fontSize: 13 }}>
            La reapertura está bloqueada:{' '}
            {cuponVigente.estado !== 'pendiente'
              ? `el cupón está en estado "${cuponVigente.estado}"`
              : `el cupón tiene ${pagosDefinitivos.length} pago(s) acreditado(s) o conciliado(s) con Pronto Pago`}
            . Si necesitás corregir, resolvé esos pagos por el circuito de pagos
            o creá una DDJJ adicional.
          </Alert>
        )}
      </Paper>

      {/* Nómina desactualizada: empleados activos que no figuran en este borrador (ADR #044) */}
      {esBorrador && faltantes.length > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={
                sincronizarNomina.isPending ? <CircularProgress size={16} /> : <GroupAddIcon />
              }
              onClick={handleSincronizar}
              disabled={sincronizarNomina.isPending}
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Agregar a la nómina
            </Button>
          }
        >
          Hay <strong>{faltantes.length}</strong>{' '}
          {faltantes.length === 1 ? 'empleado activo' : 'empleados activos'} de la empresa que no{' '}
          {faltantes.length === 1 ? 'figura' : 'figuran'} en esta DDJJ. Se agregan con sueldo 0
          para que los completes; los sueldos ya cargados no se modifican.
        </Alert>
      )}
      {syncResultado && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSyncResultado(null)}>
          {syncResultado.agregados > 0
            ? `Se agregaron ${syncResultado.agregados} empleado(s) a la nómina. Completá sus sueldos y guardá.`
            : 'La nómina ya estaba al día: no había empleados para agregar.'}
          {syncResultado.bajas > 0 &&
            ` Atención: ${syncResultado.bajas} empleado(s) de la nómina figuran dados de baja; revisalos antes de confirmar.`}
        </Alert>
      )}
      {sincronizarNomina.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {mapSupabaseError(sincronizarNomina.error, 'No se pudo sincronizar la nómina.')}
        </Alert>
      )}

      {/* Grilla de empleados */}
      <Card sx={{ mb: 2 }}>
        <DataGrid
          rows={detalle}
          columns={columns}
          loading={loadingDetalle}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
          sx={{ border: 0 }}
        />
      </Card>

      {/* Totales */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {detalle.length} empleado{detalle.length !== 1 ? 's' : ''} en nómina
          </Typography>
          <Stack direction="row" spacing={3} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Monto total:
            </Typography>
            <Typography variant="h6" fontWeight={700} color="primary">
              {formatCurrency(montoTotal)}
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      {/* Acciones (solo borrador) */}
      {esBorrador && (
        <>
          {sueldoErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSueldoErrors([])}>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Corregí los siguientes sueldos antes de guardar:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {sueldoErrors.slice(0, 5).map((e, i) => (
                  <li key={i}>
                    <Typography variant="body2">{e}</Typography>
                  </li>
                ))}
                {sueldoErrors.length > 5 && (
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      ... y {sueldoErrors.length - 5} más.
                    </Typography>
                  </li>
                )}
              </Box>
            </Alert>
          )}
          {(guardarDetalle.isError || confirmarDDJJ.isError) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {(guardarDetalle.error as Error)?.message ??
                (confirmarDDJJ.error as Error)?.message ??
                'Error al guardar'}
            </Alert>
          )}

          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => setOpenImport(true)}
              disabled={detalle.length === 0}
            >
              Importar sueldos
            </Button>

            <Tooltip title={isDirty ? '' : 'No hay cambios pendientes'}>
              <span>
                <Button
                  variant="outlined"
                  startIcon={
                    guardarDetalle.isPending ? (
                      <CircularProgress size={18} />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  onClick={handleGuardar}
                  disabled={!isDirty || guardarDetalle.isPending}
                >
                  Guardar cambios
                </Button>
              </span>
            </Tooltip>

            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleOutlineIcon />}
              onClick={() => setConfirmOpen(true)}
              disabled={detalle.length === 0}
            >
              Confirmar DDJJ
            </Button>

            <Button
              variant="outlined"
              color="error"
              onClick={() => setDeleteConfirmOpen(true)}
            >
              Eliminar borrador
            </Button>
          </Stack>
        </>
      )}

      {/* Dialog: Confirmar DDJJ */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs">
        <DialogTitle>Confirmar DDJJ</DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            <Typography>
              ¿Confirmás la declaración jurada de{' '}
              <strong>{planilla.empresa?.razon_social}</strong> para el período{' '}
              <strong>{planilla.periodo?.periodo}</strong>
              {planilla.numero > 1 ? ` (DDJJ #${planilla.numero})` : ''}?
            </Typography>
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Empleados:
              </Typography>
              <Typography variant="body2">{detalle.length}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Monto total:
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatCurrency(montoTotal)}
              </Typography>
            </Stack>
            <Alert severity="warning" sx={{ mt: 1, fontSize: 13 }}>
              Esta operación es <strong>irreversible</strong>. Una vez confirmada,
              la DDJJ no podrá modificarse (RN-21).
            </Alert>
            {faltantes.length > 0 && (
              <Alert severity="warning" sx={{ fontSize: 13 }}>
                Faltan <strong>{faltantes.length}</strong>{' '}
                {faltantes.length === 1 ? 'empleado activo' : 'empleados activos'} en esta
                nómina: si confirmás ahora no figurarán en esta DDJJ. Cerrá este
                diálogo y usá <strong>"Agregar a la nómina"</strong> si corresponde.
              </Alert>
            )}
            {isDirty && (
              <Alert severity="info" sx={{ fontSize: 13 }}>
                Los cambios de sueldo pendientes se guardarán automáticamente.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmar}
            disabled={confirmarDDJJ.isPending}
            startIcon={
              confirmarDDJJ.isPending ? <CircularProgress size={18} /> : null
            }
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Reabrir DDJJ */}
      <Dialog
        open={reopenOpen}
        onClose={() => {
          setReopenOpen(false);
          setReopenError(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reabrir DDJJ confirmada</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Alert severity="warning" sx={{ fontSize: 13 }}>
              Esta acción <strong>anula el cupón vigente</strong> y vuelve la DDJJ
              a <strong>borrador</strong>. Al reconfirmarla se emitirá un cupón
              nuevo (el anulado queda en historia). Queda registrada en auditoría.
            </Alert>
            {pagosPorRechazar.length > 0 && (
              <Alert severity="info" sx={{ fontSize: 13 }}>
                El cupón tiene <strong>{pagosPorRechazar.length} pago(s) pendiente(s)</strong>:
                se <strong>rechazarán automáticamente</strong> con el motivo de la
                reapertura (la empresa verá el motivo).
              </Alert>
            )}
            <Typography variant="body2">
              Empresa: <strong>{planilla.empresa?.razon_social}</strong> · Período:{' '}
              <strong>{planilla.periodo?.periodo}</strong>
              {planilla.numero > 1 ? ` · DDJJ #${planilla.numero}` : ''}
            </Typography>
            {cuponVigente && (
              <Typography variant="body2" color="text.secondary">
                Cupón a anular: <code>{cuponVigente.codigo}</code> ·{' '}
                {formatCurrency(cuponVigente.monto_total)}
              </Typography>
            )}
            <TextField
              label="Motivo de la reapertura"
              placeholder="Ej: La empresa cargó mal el sueldo de Juan Pérez (se confirmó por error)"
              required
              fullWidth
              multiline
              rows={3}
              value={reopenMotivo}
              onChange={(e) => {
                setReopenMotivo(e.target.value);
                setReopenError(null);
              }}
              error={!!reopenError}
              helperText={reopenError ?? 'Mínimo 10 caracteres. Se guarda en el cupón anulado.'}
              inputProps={{ maxLength: 500 }}
            />
            {reabrirDDJJ.isError && (
              <Alert severity="error">
                {(reabrirDDJJ.error as Error)?.message ?? 'Error al reabrir'}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setReopenOpen(false);
              setReopenError(null);
            }}
            disabled={reabrirDDJJ.isPending}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleReabrir}
            disabled={reabrirDDJJ.isPending}
            startIcon={
              reabrirDDJJ.isPending ? <CircularProgress size={18} /> : <LockOpenIcon />
            }
          >
            Reabrir DDJJ
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Eliminar borrador */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          deleteDDJJ.reset();
        }}
        maxWidth="xs"
      >
        <DialogTitle>Eliminar borrador</DialogTitle>
        <DialogContent>
          ¿Eliminar la DDJJ borrador de{' '}
          <strong>{planilla.empresa?.razon_social}</strong> para el período{' '}
          <strong>{planilla.periodo?.periodo}</strong>? Esta acción no se puede deshacer.
          {pagosPorRechazarAlEliminar > 0 && (
            <Alert severity="warning" sx={{ mt: 2, fontSize: 13 }}>
              Hay <strong>{pagosPorRechazarAlEliminar} pago(s) pendiente(s)</strong> sobre esta
              DDJJ: se <strong>rechazarán automáticamente</strong> al eliminarla.
            </Alert>
          )}
          {deleteDDJJ.isError && (
            <Alert severity="error" sx={{ mt: 2, fontSize: 13 }}>
              {mapSupabaseError(deleteDDJJ.error, 'No se pudo eliminar la DDJJ. Volvé a intentar; si persiste, avisá al administrador.')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false);
              deleteDDJJ.reset();
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleteDDJJ.isPending}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Importar sueldos desde CSV/XLSX (#496) */}
      <ImportarSueldosDialog
        open={openImport}
        onClose={() => setOpenImport(false)}
        nomina={detalle.map((row) => ({ id: row.id, cuil: row.cuil }))}
        onApply={(updates) =>
          setSueldoMap((prev) => ({ ...prev, ...updates }))
        }
      />
    </Box>
  );
}
